import React, { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import API_BASE from '../config';
import {
  CheckCircle,
  Copy,
  ArrowRight,
  ShieldCheck,
  Star,
  Calendar,
  Loader2,
  Users,
  Briefcase,
  Video,
} from 'lucide-react';

const SESSION_TYPES = [
  {
    id: 'one_to_one',
    label: '1 : 1 Session',
    description: 'Deep, personalised coaching.',
    icon: Users,
  },
  {
    id: 'individual_corporate',
    label: 'Corporate Training',
    description: 'For teams and organisations.',
    icon: Briefcase,
  },
  {
    id: 'group_training',
    label: 'Group Training',
    description: 'Live online workshops.',
    icon: Video,
  },
];



const BookSession = () => {
  const navigate = useNavigate();
  const location = useLocation();
  // Default to passed state type or fall back to 'one_to_one'
  const [selectedType, setSelectedType] = useState(location.state?.type || 'one_to_one');
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    goals: '',
    consent: false,
  });

  // Date & Slot State
  const [selectedDate, setSelectedDate] = useState('');
  const [slots, setSlots] = useState([]);
  const [selectedSlot, setSelectedSlot] = useState('');
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [slotError, setSlotError] = useState('');

  // Submission State
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [generatedId, setGeneratedId] = useState('');
  const [copied, setCopied] = useState(false);

  const getAmount = () => {
    return selectedType === 'one_to_one' ? 999 : 0;
  };

  // Check if current type is a Proposal type (Corporate or Group)
  const isProposalMode = selectedType === 'individual_corporate' || selectedType === 'group_training';

  // 🔹 Date change → fetch slots
  const handleDateChange = (e) => {
    const value = e.target.value;

    // Validate: No Sundays (0 represents Sunday)
    if (value && new Date(value).getDay() === 0) {
      alert("Bookings are available from Monday to Saturday only.");
      e.target.value = ""; // Reset input
      setSelectedDate("");
      setSlots([]);
      return;
    }

    setSelectedDate(value);
    setSelectedSlot('');
    setSlots([]);
    setSlotError('');

    if (!value) return;

    setLoadingSlots(true);
    try {
      (async () => {
        console.log('[BookSession] Fetching slots for date:', value);
        const res = await fetch(`${API_BASE}/api/slots?date=${value}`);
        const result = await res.json();
        console.log('[BookSession] Slots response:', res.status, result);

        if (res.ok) {
          setSlots(result.slots || []);
        } else {
          setSlotError(result.message || 'Unable to load slots.');
        }
        setLoadingSlots(false);
      })();
    } catch (err) {
      console.error('[BookSession] Error fetching slots:', err);
      setSlotError('Unable to load slots. Please try again.');
      setLoadingSlots(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    let newValue = type === 'checkbox' ? checked : value;

    // Enforce 10-digit numeric only for phone
    if (name === 'phone') {
      const numericValue = value.replace(/[^0-9]/g, '').slice(0, 10);
      newValue = numericValue;
    }

    setFormData((prev) => ({ ...prev, [name]: newValue }));
  };

  // 🔹 Submit booking → hit backend
  const handleSubmit = async (e) => {
    e.preventDefault();

    // 🔹 VALIDATION
    if (!formData.consent) {
      alert('Please accept the consent to continue.');
      return;
    }
    if (!formData.email.includes('@')) {
      alert('Please enter a valid email address.');
      return;
    }
    if (formData.phone.length !== 10) {
      alert('Please enter a valid 10-digit phone number.');
      return;
    }

    // specific checks for 1:1 booking
    if (!isProposalMode) {
      if (!selectedDate) {
        alert('Please choose a preferred date.');
        return;
      }
      if (!selectedSlot) {
        alert('Please select a time slot.');
        return;
      }
    }

    setSubmitting(true);

    const currentType = SESSION_TYPES.find((t) => t.id === selectedType);

    try {
      let res;
      let result;

      if (isProposalMode) {
        // === PROPOSAL FLOW ===
        const payload = {
          name: formData.name,
          email: formData.email,
          phone: formData.phone,
          sessionType: currentType?.label || selectedType,
          message: formData.goals, // treating "goals" as "requirements/message"
        };
        console.log('[BookSession] Sending PROPOSAL payload:', payload);

        res = await fetch(`${API_BASE}/api/contact/proposal`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload),
        });

      } else {
        // === BOOKING FLOW (1:1) ===
        const payload = {
          ...formData,
          sessionType: currentType?.label || selectedType,
          date: selectedDate,
          time: selectedSlot,
          message: formData.goals,
          amount: getAmount(),
          number: formData.phone,
        };
        console.log('[BookSession] Sending BOOKING payload:', payload);

        res = await fetch(`${API_BASE}/api/payment/initiate`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload),
        });
      }

      result = await res.json().catch(() => null);
      console.log('[BookSession] Response:', res.status, result);

      if (res.ok && result && result.success) {
        // CHECK FOR REDIRECT URL (For Payments)
        if (result.data?.instrumentResponse?.redirectInfo?.url) {
          console.log("Redirecting to PhonePe:", result.data.instrumentResponse.redirectInfo.url);
          window.location.href = result.data.instrumentResponse.redirectInfo.url;
          return; // Stop further execution
        }

        // For proposal or free confirmed bookings
        setGeneratedId(result.bookingCode || 'PROPOSAL-SENT');
        setSubmitted(true);
      } else {
        alert(
          (result && result.message) ||
          'Something went wrong. Please try again.'
        );
      }
    } catch (err) {
      console.error('[BookSession] Error submitting form:', err);
      alert(`Connection Error: ${err.message}. Please check your network or try again.`);
    } finally {
      setSubmitting(false);
    }
  };

  const copyId = () => {
    if (!generatedId) return;
    navigator.clipboard.writeText(generatedId);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  // ✅ Success screen
  // ✅ Success screen
  if (submitted) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 p-4 font-sans">
        <div className="bg-white p-8 md:p-12 text-center max-w-lg shadow-[0_20px_50px_rgba(0,0,0,0.1)] rounded-3xl border border-white/50 w-full relative overflow-hidden">

          {/* Decorative background element */}
          <div className="absolute top-0 left-0 w-full h-1.5 bg-gradient-to-r from-purple-600 to-indigo-600" />

          <div className="mx-auto w-20 h-20 bg-green-50 text-green-500 rounded-full flex items-center justify-center mb-6 shadow-sm ring-8 ring-green-50/50">
            <CheckCircle className="w-10 h-10" />
          </div>

          <h2 className="text-3xl font-bold text-gray-900 mb-3 tracking-tight">
            {generatedId === 'PROPOSAL-SENT'
              ? 'Proposal Submitted'
              : 'Booking Confirmed'}
          </h2>
          <p className="text-gray-500 mb-8 text-sm md:text-base leading-relaxed max-w-xs mx-auto">
            {generatedId === 'PROPOSAL-SENT'
              ? 'Thank you for your interest. We have received your details and will get back to you shortly.'
              : <>We have successfully confirmed your booking. Session details and joining instructions have been sent to your <strong>registered email</strong>.</>}
          </p>

          {/* ID Card - ONLY for Booking Mode */}
          {generatedId !== 'PROPOSAL-SENT' && (
            <div className="bg-gray-50 p-6 rounded-2xl border border-gray-100 mb-8 relative group hover:border-purple-200 transition-colors">
              <p className="text-[10px] font-bold uppercase text-gray-400 mb-3 tracking-widest">
                Your Booking ID
              </p>

              <div className="flex items-center justify-between gap-3 bg-white p-3 rounded-xl border border-gray-200 shadow-sm">
                <span className="text-xs md:text-sm font-mono font-bold text-gray-700 break-all text-center flex-1">
                  {generatedId}
                </span>
                <button
                  onClick={copyId}
                  className="p-2 hover:bg-gray-50 rounded-lg transition-all text-gray-400 hover:text-purple-600 active:scale-95 flex-shrink-0 border-l border-gray-100 pl-3"
                  title="Copy to clipboard"
                >
                  {copied ? (
                    <CheckCircle className="w-5 h-5 text-green-500" />
                  ) : (
                    <Copy className="w-5 h-5" />
                  )}
                </button>
              </div>

              <p className="text-[10px] text-gray-400 mt-3 font-bold uppercase tracking-wider flex items-center justify-center gap-1.5">
                <ShieldCheck className="w-3 h-3 text-purple-500" /> Please retain this ID for reference
              </p>
            </div>
          )}

          <button
            onClick={() => navigate('/')}
            className="w-full bg-gray-900 text-white font-bold uppercase py-4 rounded-xl text-xs tracking-widest hover:bg-black transition-all shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 flex items-center justify-center gap-2"
          >
            Return to Home <ArrowRight className="w-4 h-4 ml-1" />
          </button>
        </div>
      </div>
    );
  }

  // ✅ Main UI
  return (
    <div className="flex flex-col lg:flex-row min-h-screen bg-white">
      {/* Left Side */}
      <div className="lg:w-5/12 bg-exiser-primary text-white p-10 lg:p-16 flex flex-col justify-between relative overflow-hidden">
        <div className="absolute inset-0 bg-[url('https://www.transparenttextures.com/patterns/stardust.png')] opacity-20" />

        <div className="relative z-10">
          <div className="inline-flex items-center gap-2 bg-exiser-accent/20 border border-exiser-accent/50 rounded-full px-3 py-1 mb-6">
            <Star className="w-3 h-3 text-exiser-accent fill-current" />
            <span className="text-[10px] font-bold uppercase tracking-widest text-exiser-accent">
              Limited Availability
            </span>
          </div>
          <h1 className="text-4xl lg:text-5xl font-bold uppercase mb-6 leading-tight">
            Reserve Your <br />
            <span className="text-exiser-accent">Growth Session</span>
          </h1>
          <p className="font-sans text-purple-200 mb-8 leading-relaxed text-sm lg:text-base">
            Choose the format that suits you best. All sessions are conducted
            live via our secure online platform.
          </p>
        </div>

        <div className="relative z-10 space-y-5 hidden lg:block">
          <div className="flex items-center gap-4">
            <div className="w-8 h-8 rounded-full bg-exiser-accent flex items-center justify-center text-exiser-primary font-bold text-sm">
              1
            </div>
            <p className="text-sm font-bold uppercase tracking-wide">
              Select Session Type
            </p>
          </div>
          <div className="flex items-center gap-4">
            <div className="w-8 h-8 rounded-full bg-exiser-accent flex items-center justify-center text-exiser-primary font-bold text-sm">
              2
            </div>
            <p className="text-sm font-bold uppercase tracking-wide">
              Choose Date &amp; Time
            </p>
          </div>
          <div className="flex items-center gap-4">
            <div className="w-8 h-8 rounded-full bg-exiser-accent flex items-center justify-center text-exiser-primary font-bold text-sm">
              3
            </div>
            <p className="text-sm font-bold uppercase tracking-wide">
              Online Link Sent
            </p>
          </div>
        </div>
      </div>

      {/* Right Side - Form */}
      <div className="lg:w-7/12 flex items-center justify-center p-6 md:p-12 bg-gray-50">
        <div className="max-w-xl w-full bg-white p-8 rounded-2xl shadow-xl border border-gray-100">
          <div className="mb-6 pb-4 border-b border-gray-100">
            <h2 className="text-xl font-bold uppercase text-gray-900">
              Session Details
            </h2>
            <p className="text-xs text-gray-500 mt-1">
              Please fill in your details accurately.
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Session Type Selection */}
            <div>
              <label className="text-[10px] font-bold uppercase text-gray-500 tracking-wider mb-2 block">
                Select Session Type
              </label>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                {SESSION_TYPES.map((type) => (
                  <button
                    key={type.id}
                    type="button"
                    onClick={() => setSelectedType(type.id)}
                    className={`flex flex-col items-center justify-center p-3 rounded-xl border text-center transition-all ${selectedType === type.id
                      ? 'border-exiser-primary bg-purple-50 text-exiser-primary shadow-sm'
                      : 'border-gray-200 bg-gray-50 text-gray-500 hover:bg-gray-100'
                      }`}
                  >
                    <type.icon
                      className={`w-5 h-5 mb-2 ${selectedType === type.id
                        ? 'text-exiser-primary'
                        : 'text-gray-400'
                        }`}
                    />
                    <span className="text-xs font-bold leading-tight">
                      {type.label}
                    </span>
                  </button>
                ))}
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <label className="text-[10px] font-bold uppercase text-gray-500 tracking-wider">
                  Full Name
                </label>
                <input
                  type="text"
                  name="name"
                  required
                  value={formData.name}
                  onChange={handleInputChange}
                  placeholder="John Doe"
                  className="w-full bg-gray-50 border border-gray-200 rounded-lg px-3 py-2.5 text-sm text-gray-900 focus:outline-none focus:border-exiser-primary focus:ring-1 focus:ring-exiser-primary transition-all placeholder-gray-300"
                />
              </div>
              <div className="space-y-1.5">
                <label className="text-[10px] font-bold uppercase text-gray-500 tracking-wider">
                  Email
                </label>
                <input
                  type="email"
                  name="email"
                  required
                  value={formData.email}
                  onChange={handleInputChange}
                  placeholder="john@example.com"
                  className="w-full bg-gray-50 border border-gray-200 rounded-lg px-3 py-2.5 text-sm text-gray-900 focus:outline-none focus:border-exiser-primary focus:ring-1 focus:ring-exiser-primary transition-all placeholder-gray-300"
                />
              </div>
            </div>

            <div className="space-y-1.5">
              <label className="text-[10px] font-bold uppercase text-gray-500 tracking-wider">
                Phone
              </label>
              <input
                type="tel"
                name="phone"
                required
                value={formData.phone}
                onChange={handleInputChange}
                placeholder="10-digit number"
                className="w-full bg-gray-50 border border-gray-200 rounded-lg px-3 py-2.5 text-sm text-gray-900 focus:outline-none focus:border-exiser-primary focus:ring-1 focus:ring-exiser-primary transition-all placeholder-gray-300"
                maxLength={10}
                inputMode="numeric"
              />
            </div>

            {/* Date Selection - ONLY FOR 1:1 Booking */}
            {!isProposalMode && (
              <div className="space-y-1.5">
                <label className="text-[10px] font-bold uppercase text-gray-500 tracking-wider">
                  Preferred Date
                </label>
                <div className="relative">
                  <input
                    type="date"
                    name="date"
                    min={new Date().toISOString().split("T")[0]} // Prevent past dates
                    required
                    value={selectedDate}
                    onChange={handleDateChange}
                    className="w-full bg-gray-50 border border-gray-200 rounded-lg pl-10 pr-3 py-2.5 text-sm text-gray-900 focus:outline-none focus:border-exiser-primary focus:ring-1 focus:ring-exiser-primary transition-all placeholder-gray-300 cursor-pointer"
                  />
                  <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
                </div>
              </div>
            )}

            {/* Slot Selection - ONLY FOR 1:1 Booking */}
            {!isProposalMode && selectedDate && (
              <div className="space-y-2">
                <label className="text-[10px] font-bold uppercase text-gray-500 tracking-wider flex items-center justify-between">
                  <span>Available Slots (IST)</span>
                  {loadingSlots && (
                    <span className="flex items-center gap-1 text-exiser-primary">
                      <Loader2 className="w-3 h-3 animate-spin" /> Loading...
                    </span>
                  )}
                </label>

                {!loadingSlots && slotError && (
                  <p className="text-xs text-red-500 bg-red-50 p-2 rounded">
                    {slotError}
                  </p>
                )}

                {!loadingSlots && !slotError && slots.length === 0 && (
                  <p className="text-xs text-red-400 bg-red-50 p-2 rounded">
                    No slots available for this date.
                  </p>
                )}

                {!loadingSlots && slots.length > 0 && (
                  <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
                    {slots.map((slot) => (
                      <button
                        key={slot.time}
                        type="button"
                        disabled={!slot.available}
                        onClick={() => setSelectedSlot(slot.time)}
                        className={`py-2 px-1 rounded-lg text-xs font-bold transition-all border ${!slot.available
                          ? 'bg-gray-100 text-gray-300 border-transparent cursor-not-allowed'
                          : selectedSlot === slot.time
                            ? 'bg-exiser-primary text-white border-exiser-primary shadow-md transform scale-105'
                            : 'bg-white text-gray-700 border-gray-200 hover:border-exiser-accent hover:bg-yellow-50'
                          }`}
                      >
                        {slot.time}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}

            <div className="space-y-1.5">
              <label className="text-[10px] font-bold uppercase text-gray-500 tracking-wider">
                {isProposalMode ? 'Requirements / Message' : 'Session Goals'}
              </label>
              <textarea
                name="goals"
                required
                rows={3}
                value={formData.goals}
                onChange={handleInputChange}
                placeholder="What do you want to achieve?"
                className="w-full bg-gray-50 border border-gray-200 rounded-lg px-3 py-2.5 text-sm text-gray-900 focus:outline-none focus:border-exiser-primary focus:ring-1 focus:ring-exiser-primary transition-all placeholder-gray-300 resize-none"
              />
            </div>

            <div className="flex items-start gap-3 p-3 bg-yellow-50 border border-yellow-100 rounded-lg">
              <input
                type="checkbox"
                id="consent"
                name="consent"
                checked={formData.consent}
                onChange={(e) =>
                  setFormData((prev) => ({
                    ...prev,
                    consent: e.target.checked,
                  }))
                }
                className="mt-0.5 w-4 h-4 border-gray-300 text-exiser-primary focus:ring-exiser-primary rounded"
              />
              <label
                htmlFor="consent"
                className="text-xs text-gray-600 font-sans cursor-pointer leading-tight"
              >
                I agree to be contacted by Exiser and understand that my session
                will be conducted online.
              </label>
            </div>

            <button
              type="submit"
              disabled={submitting}
              className="w-full bg-exiser-primary text-white font-bold uppercase py-3.5 rounded-lg text-sm tracking-widest hover:bg-purple-900 transition-all shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 mt-2 flex items-center justify-center gap-2 disabled:opacity-70 disabled:cursor-not-allowed"
            >
              {submitting ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                isProposalMode ? 'Request Proposal' : 'Pay ₹999 & Confirm Booking'
              )}
            </button>

            <div className="text-center pt-2">
              <p className="text-[10px] text-gray-400 flex items-center justify-center gap-1">
                <ShieldCheck className="w-3 h-3" /> 100% Confidential &amp;
                Secure
              </p>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default BookSession;